import * as React from 'react';
import {
  Children,
  cloneElement,
  forwardRef,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AnimatePresence, motion } from 'motion/react';
import type { Transition } from 'motion/react';
import clsx from 'clsx';
import mergeRefs from '../../../utils/merge-refs';
import css from './swipeable.module.scss';

export type SwipeableItemProps = React.PropsWithChildren<
  Pick<
    React.HTMLAttributes<HTMLDivElement>,
    'className' | 'style' | 'aria-hidden'
  >
>;

const SwipeableItem = forwardRef<HTMLDivElement, SwipeableItemProps>(
  function SwipeableItem(
    { className, style, 'aria-hidden': ariaHidden, children },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className={clsx(css.slide, className)}
        style={style}
        aria-hidden={ariaHidden}
      >
        {children}
      </div>
    );
  },
);

const defaultTransition = {
  type: 'tween' as const,
  duration: 0.35,
  ease: 'easeInOut' as const,
};

export type SwipeableProps = React.PropsWithChildren<{
  /** Index of the slide shown in the viewport (clamped to valid range). */
  index: number;
  className?: string;
  /**
   * When true (default), every slide stays mounted so local state inside inactive slides is kept.
   * When false, only the active slide is rendered (lighter DOM; inactive state is lost).
   */
  keepMounted?: boolean;
  /**
   * When true, the viewport height follows the active slide’s content height and animates when the
   * index changes or the active content resizes. Slides use `align-items: flex-start` on the track.
   */
  animateHeight?: boolean;
  /** Passed to `motion` for horizontal slide motion. */
  transition?: Transition;
  /** Transition for viewport height; defaults to the same as `transition`. */
  heightTransition?: Transition;
}>;

function clampIndex(index: number, count: number): number {
  if (count <= 0) {
    return 0;
  }

  return Math.min(Math.max(0, index), count - 1);
}

function isSwipeableItemElement(
  child: React.ReactElement,
): child is React.ReactElement<SwipeableItemProps> {
  return child.type === SwipeableItem;
}

function buildSlides(children: React.ReactNode): React.ReactElement[] {
  return Children.toArray(children)
    .filter(React.isValidElement)
    .map((child, i) => {
      if (isSwipeableItemElement(child as React.ReactElement)) {
        return cloneElement(child as React.ReactElement<SwipeableItemProps>, {
          key: child.key ?? `swipe-item-${i}`,
        });
      }
      return (
        <SwipeableItem
          key={(child as React.ReactElement).key ?? `swipe-item-${i}`}
        >
          {child}
        </SwipeableItem>
      );
    });
}

const slideVariants = {
  enter: (dir: number) => ({
    x: dir > 0 ? '100%' : '-100%',
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({
    x: dir > 0 ? '-100%' : '100%',
    opacity: 0,
  }),
};

type AnyRef = React.Ref<HTMLDivElement> | undefined;

function getElementRef(el: React.ReactElement): AnyRef {
  return (el as React.ReactElement<unknown> & { ref?: AnyRef }).ref;
}

const SwipeableRoot: React.FC<SwipeableProps> = (props) => {
  const {
    index,
    children,
    className,
    keepMounted = true,
    animateHeight = false,
    transition = defaultTransition,
    heightTransition: heightTransitionProp,
  } = props;

  const heightTransition = heightTransitionProp ?? transition;

  const slides = useMemo(() => buildSlides(children), [children]);
  const count = slides.length;
  const safeIndex = clampIndex(index, count);
  const prevIndexRef = useRef<number | null>(null);

  let direction = 0;
  if (prevIndexRef.current !== null && prevIndexRef.current !== safeIndex) {
    direction = safeIndex > prevIndexRef.current ? 1 : -1;
  }
  prevIndexRef.current = safeIndex;

  const slideRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const [trackViewportHeight, setTrackViewportHeight] = useState<number | null>(
    null,
  );

  useLayoutEffect(() => {
    if (!keepMounted || !animateHeight) {
      setTrackViewportHeight(null);
      return;
    }

    const el = slideRefs.current[safeIndex];
    if (!el) {
      return;
    }

    const read = () => {
      const next = Math.ceil(el.getBoundingClientRect().height);
      setTrackViewportHeight((prev) => (prev === next ? prev : next));
    };

    read();

    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [keepMounted, animateHeight, safeIndex, count]);

  const singleSlideRef = useRef<HTMLDivElement | null>(null);
  const [singleViewportHeight, setSingleViewportHeight] = useState<
    number | null
  >(null);

  useLayoutEffect(() => {
    if (keepMounted || !animateHeight) {
      setSingleViewportHeight(null);
      return;
    }

    const el = singleSlideRef.current;
    if (!el) {
      return;
    }

    const read = () => {
      const next = Math.ceil(el.getBoundingClientRect().height);
      setSingleViewportHeight((prev) => (prev === next ? prev : next));
    };

    read();

    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [keepMounted, animateHeight, safeIndex, count]);

  if (count === 0) {
    return null;
  }

  if (!keepMounted) {
    const active = slides[safeIndex];

    const activeWithRef = animateHeight
      ? cloneElement(active, {
          ref: mergeRefs((el) => {
            singleSlideRef.current = el;
          }, getElementRef(active)),
        })
      : active;

    const inner = (
      <AnimatePresence initial={false} custom={direction} mode="popLayout">
        <motion.div
          key={safeIndex}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={transition}
          style={{ width: '100%' }}
        >
          {activeWithRef}
        </motion.div>
      </AnimatePresence>
    );

    return (
      <div className={clsx(css.root, className)}>
        {animateHeight ? (
          <motion.div
            className={clsx(css.viewport, css.single)}
            initial={false}
            animate={
              singleViewportHeight != null
                ? { height: singleViewportHeight }
                : { height: 'auto' }
            }
            transition={heightTransition}
            style={{ overflow: 'hidden' }}
          >
            {inner}
          </motion.div>
        ) : (
          <div className={clsx(css.viewport, css.single)}>{inner}</div>
        )}
      </div>
    );
  }

  const slidePercent = 100 / count;
  const xPercent = -(slidePercent * safeIndex);

  const track = (
    <motion.div
      className={clsx(css.track, animateHeight && css.trackAlignStart)}
      style={{ width: `${count * 100}%` }}
      animate={{ x: `${xPercent}%` }}
      transition={transition}
    >
      {slides.map((slide, i) => {
        const origRef = getElementRef(slide);

        return cloneElement(slide, {
          key: slide.key ?? i,
          ref: animateHeight
            ? mergeRefs((el) => {
                slideRefs.current[i] = el;
              }, origRef)
            : origRef,
          style: {
            ...(slide.props.style ?? {}),
            width: `${slidePercent}%`,
          },
          'aria-hidden': i !== safeIndex,
        });
      })}
    </motion.div>
  );

  return (
    <div className={clsx(css.root, className)}>
      {animateHeight ? (
        <motion.div
          className={css.viewport}
          initial={false}
          animate={
            trackViewportHeight != null
              ? { height: trackViewportHeight }
              : { height: 'auto' }
          }
          transition={heightTransition}
          style={{ overflow: 'hidden' }}
        >
          {track}
        </motion.div>
      ) : (
        <div className={css.viewport}>{track}</div>
      )}
    </div>
  );
};

const Swipeable = Object.assign(SwipeableRoot, { Item: SwipeableItem });

export default Swipeable;
export { SwipeableItem };
